"use client";

import { useState, useMemo } from "react";
import type { PnlSnapshotRecordDto, PnlSnapshotSseEventDto } from "@cede/types";
import { PnlSnapshotSseEventType } from "@cede/types";
import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useCampaign } from "@/hooks";
import { getPnlSnapshotQueryKey, type PnlSnapshotQueryData } from "@/hooks/campaign/pnl/usePnlSnapshot";
import {
  applyTradeSnapshot,
  createEmptyHydrationState,
  type PnlHydrationState,
} from "@/utils/pnl/pnl-snapshot-reducer";
import { useQueryClient } from "@tanstack/react-query";
import Decimal from "decimal.js";
import { Activity, Send, UserPlus } from "lucide-react";
import { useParams } from "next/navigation";

const randomHex = () => `0x${Array.from({ length: 40 }, () => Math.floor(Math.random() * 16).toString(16)).join("")}`;
/** Synthetic participants need an identity too — the wire carries one per user. */
const randomHubUserId = () => Math.floor(Math.random() * 1_000_000);

/** Labelled number input row */
const Field = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
  <div className="flex items-center gap-2">
    <label className="text-xs text-cede-text-secondary whitespace-nowrap w-[140px] shrink-0">{label}</label>
    <Input type="number" step="any" value={value} onChange={(e) => onChange(e.target.value)} className="h-7 text-xs" />
  </div>
);

/**
 * Dev-only scaffold that injects a synthetic PnL snapshot into the React Query
 * cache — same shape the SSE handler produces. The cache value is
 * `PnlSnapshotQueryData` (see `usePnlSnapshot`), so we reuse its reducer
 * (`applyTradeSnapshot`) to fold the injected snapshot into the existing
 * hydration state instead of overwriting it with a stale legacy shape.
 */
export const PnlSnapshotFeature = () => {
  const queryClient = useQueryClient();
  const { id: campaignName } = useParams();
  const { currentEpoch } = useCampaign(campaignName as string);
  const epochId = currentEpoch?.id;
  const isActive = !!campaignName && epochId != null;

  // Find the active cache entry for this campaign+epoch (there can be at most
  // one key shape — `[QueryKeys.PNL_SNAPSHOTS, campaignName, epochId, exchangeId]`
  // — but `exchangeId` varies, so grab the first populated entry we find.
  const { queryKey, existingData } = useMemo(() => {
    if (!isActive) return { queryKey: null, existingData: undefined };

    const matches = queryClient.getQueriesData<PnlSnapshotQueryData>({
      queryKey: getPnlSnapshotQueryKey(campaignName as string, epochId as number),
    });

    const populated = matches.find(([, data]) => data?.hydrationState != null);
    const fallback = matches[0];
    const chosen = populated ?? fallback;

    return {
      queryKey: chosen?.[0] ?? null,
      existingData: chosen?.[1] ?? undefined,
    };
  }, [isActive, queryClient, campaignName, epochId]);

  const existingAddresses = useMemo<string[]>(() => {
    if (!existingData?.hydrationState) return [];
    return Array.from(existingData.hydrationState.users.values()).map((u) => u.defiAddress);
  }, [existingData]);

  // Form state
  const [defiAddress, setDefiAddress] = useState("");
  const [hubUserId, setHubUserId] = useState(randomHubUserId);
  const [pnl, setPnl] = useState("0");
  const [score, setScore] = useState("0");
  const [tradedVolumeUSD, setTradedVolumeUSD] = useState("0");
  const [pendingTokenAmount, setPendingTokenAmount] = useState("0");
  const [pendingTokenPurchasePriceUSD, setPendingTokenPurchasePriceUSD] = useState("0");
  const [tokenPair, setTokenPair] = useState("BTC/USDT");
  const [delay, setDelay] = useState("0");

  const hasAddress = defiAddress.length > 0;

  /** Select an existing user and pre-fill from their latest snapshot */
  const handleSelectAddress = (addr: string) => {
    setDefiAddress(addr);
    const user = existingData?.hydrationState?.users.get(addr.toLowerCase());
    const lastSnap = user?.snapshots[user.snapshots.length - 1];
    if (lastSnap) {
      setPnl(lastSnap.pnl.toString());
      setScore(lastSnap.score.toString());
      setTradedVolumeUSD(lastSnap.tradedVolumeUSD.toString());
      setPendingTokenAmount(lastSnap.pendingTokenAmount.toString());
      setPendingTokenPurchasePriceUSD(lastSnap.pendingTokenPurchasePriceUSD.toString());
      setTokenPair(lastSnap.tokenPair);
    }
  };

  /** Generate a new random address and reset fields */
  const handleNewAddress = () => {
    setDefiAddress(randomHex());
    setHubUserId(randomHubUserId());
    setPnl("0");
    setScore("0");
    setTradedVolumeUSD("0");
    setPendingTokenAmount("0");
    setPendingTokenPurchasePriceUSD("0");
  };

  /** Fold the synthetic snapshot into the cached hydration state via the reducer. */
  const injectSnapshot = () => {
    if (!queryKey || !defiAddress) return;

    const snapshot: PnlSnapshotRecordDto = {
      campaignName: campaignName as string,
      epochId: epochId!,
      pnl: new Decimal(pnl || "0"),
      score: new Decimal(score || "0"),
      tradedVolumeUSD: new Decimal(tradedVolumeUSD || "0"),
      pendingTokenAmount: new Decimal(pendingTokenAmount || "0"),
      pendingTokenPurchasePriceUSD: new Decimal(pendingTokenPurchasePriceUSD || "0"),
      tokenPair,
      createdAt: new Date(),
    };

    const sseEvent: PnlSnapshotSseEventDto = {
      type: PnlSnapshotSseEventType.PNL_SNAPSHOT,
      payload: {
        campaignName: campaignName as string,
        epochId: epochId!,
        users: [{ defiAddress, hubUserId, snapshots: [snapshot] }],
      },
    };
    // Keep the SSE-event shape in sync so this debug scaffold matches what the
    // real handler produces — handy when diffing during dev.
    void sseEvent;

    queryClient.setQueryData<PnlSnapshotQueryData>(queryKey, (prev) => {
      const baseState: PnlHydrationState = prev?.hydrationState ?? createEmptyHydrationState();
      const nextHydrationState = applyTradeSnapshot(baseState, snapshot, defiAddress);

      return {
        hydrationState: nextHydrationState,
        userCount: prev?.userCount ?? 0,
        rank: prev?.rank ?? null,
      };
    });
  };

  const handleInject = () => {
    const delaySec = parseFloat(delay) || 0;
    if (delaySec > 0) {
      setTimeout(injectSnapshot, delaySec * 1000);
    } else {
      injectSnapshot();
    }
  };

  const statusText = isActive
    ? `PnL: ${campaignName} (Epoch ${epochId}) — ${existingAddresses.length} users`
    : "No PnL data on this page";

  return (
    <AccordionItem
      value="pnl-snapshot"
      className="rounded-large border-container bg-cede-bg-component/60 overflow-hidden"
    >
      <AccordionTrigger className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4" />
          <span className="font-semibold">PnL Snapshot</span>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4 py-3 space-y-3">
        <div className="text-xs text-cede-text-secondary bg-cede-bg-primary/50 rounded-md px-2.5 py-1.5">
          {statusText}
        </div>

        <p className="text-xs text-cede-text-secondary">
          Simulate a snapshot update SSE event. Select a user and we prefill with his latest snapshot.
        </p>

        {/* User selection */}
        <div className="space-y-1.5">
          <label htmlFor="pnl-snapshot-user" className="text-xs text-cede-text-secondary font-medium">
            User
          </label>
          <div className="flex gap-1.5">
            <select
              id="pnl-snapshot-user"
              className="flex-1 h-7 rounded-md border border-cede-border-container bg-cede-bg-primary text-xs text-cede-text-primary px-2 truncate"
              value={defiAddress}
              onChange={(e) => handleSelectAddress(e.target.value)}
              disabled={!isActive || !existingAddresses.length}
            >
              <option value="">Select existing user...</option>
              {existingAddresses.map((addr) => (
                <option key={addr} value={addr}>
                  {addr.slice(0, 6)}...{addr.slice(-4)}
                </option>
              ))}
            </select>
            <Button
              variant="secondary"
              size="small"
              className="h-7 px-2 text-xs shrink-0"
              onClick={handleNewAddress}
              disabled={!isActive}
            >
              <UserPlus className="h-3 w-3 mr-1" />
              New
            </Button>
          </div>
          {hasAddress && <div className="text-[10px] text-cede-text-secondary font-mono truncate">{defiAddress}</div>}
        </div>

        {/* Snapshot fields */}
        <div className="space-y-1.5">
          <Field label="PnL (realized)" value={pnl} onChange={setPnl} />
          <Field label="Score" value={score} onChange={setScore} />
          <Field label="Traded Volume USD" value={tradedVolumeUSD} onChange={setTradedVolumeUSD} />
          <Field label="Pending Token Amt" value={pendingTokenAmount} onChange={setPendingTokenAmount} />
          <Field
            label="Purchase Price USD"
            value={pendingTokenPurchasePriceUSD}
            onChange={setPendingTokenPurchasePriceUSD}
          />
          <div className="flex items-center gap-2">
            <label
              htmlFor="pnl-token-pair"
              className="text-xs text-cede-text-secondary whitespace-nowrap w-[140px] shrink-0"
            >
              Token Pair
            </label>
            <Input
              id="pnl-token-pair"
              value={tokenPair}
              onChange={(e) => setTokenPair(e.target.value)}
              className="h-7 text-xs"
            />
          </div>
        </div>

        {/* Delay */}
        <Field label="Delay (seconds)" value={delay} onChange={setDelay} />

        {/* Submit */}
        <Button
          variant="secondary"
          size="medium"
          className="w-full justify-start"
          onClick={handleInject}
          disabled={!isActive || !hasAddress}
        >
          <Send className="h-4 w-4 mr-2" />
          Inject Snapshot Event
        </Button>
      </AccordionContent>
    </AccordionItem>
  );
};
